/**
 * Schema.org structured data helpers for SEO
 * Generates JSON-LD objects for various schema types
 */

export function websiteJsonLd(baseUrl: string, siteName: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: baseUrl,
    name: siteName,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/domains?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function orgJsonLd(baseUrl: string, siteName: string, logoUrl?: string, sameAs?: string[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: baseUrl,
    name: siteName,
    logo: logoUrl || `${baseUrl}/favicons/favicon.ico`,
    ...(sameAs && sameAs.length > 0 && { sameAs: sameAs }),
  };
}

export function breadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function productLD(params: {
  name: string;
  url: string;
  binPrice?: number;
  currency?: string;
  availability?: string;
  sku?: string;
  brandName?: string;
  imageUrl?: string;
}) {
  const {
    name,
    url,
    binPrice,
    currency = 'USD',
    availability = 'https://schema.org/InStock',
    sku,
    brandName = 'Attain Domains',
    imageUrl,
  } = params;

  const product: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: name,
    url: url,
    ...(sku && { sku: sku }),
    ...(imageUrl && { image: imageUrl }),
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
  };

  // Add offer with or without price
  const offer: any = {
    '@type': 'Offer',
    url: url,
    availability: availability,
  };

  if (binPrice && binPrice > 0) {
    offer.price = binPrice;
    offer.priceCurrency = currency;
  }

  product.offers = offer;

  return product;
}

export function faqLD(qas: Array<{ q: string; a: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qas.map((qa) => ({
      '@type': 'Question',
      name: qa.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: qa.a,
      },
    })),
  };
}

/**
 * Renders structured data as JSON string for script tag
 * @param obj The JSON-LD object to stringify
 * @returns JSON string for dangerouslySetInnerHTML
 */
export function renderLD(obj: any): string {
  return JSON.stringify(obj);
}
